import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { usePWA } from '@/contexts/PWAContext';
import LanguageSwitcher from './LanguageSwitcher';
import { NotificationBell } from './notifications/NotificationBell';
import { Button } from '@/components/ui/button';
import { LogOut, LayoutDashboard, PlusCircle, Instagram, Menu, X, Smartphone } from 'lucide-react';

import TrabdjuLogo from './TrabdjuLogo';

const NewBadge = ({ color, label }) => (
  <span className={`text-[10px] font-bold text-white ${color} rounded px-1.5 py-0.5`}>{label}</span>
);

export default function Navbar() {
  const { t } = useTranslation();
  const { user, userType, logout } = useAuth();
  const pwa = usePWA();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleLogout = async () => { setOpen(false); await logout(); navigate('/'); };
  const isActive = (path) => location.pathname === path;
  const newBadge = t('nav.newBadge');

  const links = user && userType === 'employer' ? [
    { to: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, testId: 'nav-dashboard' },
    { to: '/my-jobs', label: t('nav.myJobs'), testId: 'nav-my-jobs' },
    { to: '/applications', label: t('nav.applications'), testId: 'nav-applications' },
    { to: '/interviews', label: t('nav.interviews'), badge: 'bg-blue-600', testId: 'nav-interviews' },
    { to: '/talento-em-cabo-verde', label: t('nav.talento'), badge: 'bg-amber-500', testId: 'nav-talento-employer' },
    { to: '/purchases', label: t('nav.purchases'), testId: 'nav-purchases' },
    ...(user.role === 'admin' ? [{ to: '/admin', label: t('nav.admin'), testId: 'nav-admin', admin: true }] : []),
  ] : user && userType === 'candidate' ? [
    { to: '/jobs/search', label: t('nav.searchJobs'), testId: 'nav-search-jobs' },
    { to: '/my-applications', label: t('nav.myApplications'), testId: 'nav-my-applications' },
    { to: '/favoritos', label: t('nav.savedJobs'), testId: 'nav-favourites' },
    { to: '/candidate/profile', label: t('nav.myProfile'), testId: 'nav-my-profile' },
  ] : [
    { to: '/jobs/search', label: t('nav.searchJobs'), testId: 'nav-search-jobs' },
    { to: '/talento-em-cabo-verde', label: t('nav.talento'), badge: 'bg-amber-500', testId: 'nav-talento' },
  ];

  // Adaptive layout: measure the real width needed by the inline nav (depends on role + language)
  // and switch to the hamburger menu whenever it would not fit (phones, iPads, small laptops).
  const rowRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const neededRef = useRef(0);
  const [compact, setCompact] = useState(false);
  const linksKey = `${userType}|${user?.role}|${links.map((l) => l.label).join('|')}|${t('nav.logout')}`;

  useLayoutEffect(() => { neededRef.current = 0; setCompact(false); }, [linksKey]);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const measure = () => {
      if (!compact && leftRef.current && rightRef.current) {
        neededRef.current = leftRef.current.scrollWidth + rightRef.current.scrollWidth + 32;
      }
      if (neededRef.current) setCompact(neededRef.current > row.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [compact, linksKey]);

  useEffect(() => { if (!compact) setOpen(false); }, [compact]);

  const desktopCls = compact ? 'hidden' : 'flex';
  const mobileCls = compact ? 'flex' : 'hidden';
  const tier = links.length > 5 ? 1 : 0;

  const linkCls = (l, mobile) => {
    const active = isActive(l.to);
    const base = mobile ? 'flex items-center gap-2 px-4 py-3 rounded-lg text-base font-medium' : 'px-2.5 xl:px-3 py-2 rounded-md text-sm font-medium inline-flex items-center gap-1 whitespace-nowrap';
    if (l.admin) return `${base} ${active ? 'bg-red-50 text-red-700' : 'text-red-700 hover:bg-red-50'}`;
    return `${base} ${active ? 'bg-blue-50 text-brand' : 'text-navy hover:bg-mist hover:text-brand'}`;
  };

  const renderLink = (l, mobile = false) => (
    <Link key={l.to} to={l.to} className={linkCls(l, mobile)} data-testid={mobile ? `mobile-${l.testId}` : l.testId}>
      {l.icon && <l.icon className="h-4 w-4" />}
      {l.label}
      {l.badge && <NewBadge color={l.badge} label={newBadge} />}
    </Link>
  );

  const actions = (mobile = false) => {
    const full = mobile ? 'w-full h-12 text-base' : '';
    if (user && userType === 'employer') {
      return (
        <>
          <Button onClick={() => navigate('/create-job')} variant="cta" className={`${full}`} data-testid={mobile ? 'mobile-nav-create-job-btn' : 'nav-create-job-btn'}>
            <PlusCircle className="h-4 w-4 mr-2" />{t('dashboard.createJob')}
          </Button>
          <Button variant="outline" onClick={handleLogout} className={full} data-testid={mobile ? 'mobile-nav-logout-btn' : 'nav-logout-btn'}>
            <LogOut className="h-4 w-4 mr-2" />{t('nav.logout')}
          </Button>
        </>
      );
    }
    if (user && userType === 'candidate') {
      return (
        <Button variant="outline" onClick={handleLogout} className={full} data-testid={mobile ? 'mobile-nav-logout-btn' : 'nav-logout-btn'}>
          <LogOut className="h-4 w-4 mr-2" />{t('nav.logout')}
        </Button>
      );
    }
    return (
      <>
        <Button variant="outline" onClick={() => navigate('/start')} className={full} data-testid={mobile ? 'mobile-nav-login-btn' : 'nav-login-btn'}>{t('nav.login')}</Button>
        <Button variant="cta" onClick={() => navigate('/start')} className={`${full}`} data-testid={mobile ? 'mobile-nav-register-btn' : 'nav-register-btn'}>{t('nav.register')}</Button>
      </>
    );
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={rowRef} className="flex items-center justify-between h-16 gap-3">
          <div ref={leftRef} className="flex items-center min-w-0">
            <Link to="/" className="flex items-center flex-none" data-testid="nav-brand">
              <TrabdjuLogo size="sm" className="sm:!h-10" asLink={false} />
            </Link>
            <div className={`${desktopCls} items-center ml-6 xl:ml-10 space-x-1 flex-none`} data-testid="nav-desktop-links">
              {links.map((l) => renderLink(l))}
            </div>
          </div>

          <div ref={rightRef} className="flex items-center gap-1.5 sm:gap-2 flex-none">
            {user && <NotificationBell onOpen={() => setOpen(false)} />}
            <LanguageSwitcher compact={compact} />
          <div className={`${desktopCls} items-center gap-2 xl:gap-3 flex-none`}>
            <a
              href="https://www.instagram.com/trabadju.cv?igsh=d2ZmNGFlZDE4c2xi&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className={`${tier ? 'hidden 2xl:inline-flex' : 'hidden xl:inline-flex'} p-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 transition-all transform hover:scale-110`}
              aria-label="Instagram Trabadju"
            >
              <Instagram className="h-5 w-5 text-white" />
            </a>
            {/* Botão instalar PWA — desktop */}
            {pwa?.isInstallable && !pwa?.isInstalled && (
              <button
                onClick={() => pwa.install()}
                data-testid="nav-install-app-btn"
                className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0b1e44] text-white text-xs font-semibold hover:bg-[#0b1e44]/90 transition-colors border border-amber-400/40"
              >
                <Smartphone className="h-3.5 w-3.5 text-amber-400" />
                Instalar App
              </button>
            )}
            {actions(false)}
          </div>

          <div className={`${mobileCls} items-center gap-2`}>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center justify-center h-9 w-9 sm:h-11 sm:w-11 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
              aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}
              aria-expanded={open}
              aria-controls="mobile-menu"
              data-testid="nav-menu-toggle"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          </div>
        </div>
      </div>

      {open && compact && (
        <div id="mobile-menu" className="fixed inset-x-0 top-16 bottom-0 z-40 bg-white overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => renderLink(l, true))}
            <div className="border-t border-gray-100 my-3" />
            {/* Botão de instalação PWA no menu mobile */}
            {pwa?.isInstallable && !pwa?.isInstalled && (
              <button
                onClick={() => { setOpen(false); pwa.install(); }}
                data-testid="mobile-menu-install-btn"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0b1e44] text-white font-semibold text-sm w-full mb-1"
              >
                <Smartphone className="h-5 w-5 text-amber-400" />
                Instalar App no Telemóvel
                <span className="ml-auto text-[10px] font-bold bg-amber-400 text-[#0b1e44] px-2 py-0.5 rounded-full">GRÁTIS</span>
              </button>
            )}
            <div className="flex flex-col gap-2">{actions(true)}</div>
            <a
              href="https://www.instagram.com/trabadju.cv?igsh=d2ZmNGFlZDE4c2xi&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 px-4 py-3 text-sm text-gray-600"
            >
              <Instagram className="h-5 w-5 text-pink-600" />Instagram @trabadju.cv
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
